import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import Lightbox from './Lightbox';

/**
 * Shared media-preview system — ported from .claude/pp-campaign-builder-
 * prototype.html's v15 `AU` (audio engine, ~line 804-826) + `LB` (lightbox,
 * ~line 829-863), rebuilt against REAL <audio>/<video> elements instead of
 * the prototype's fake setInterval timers (task spec: "one real <audio>
 * element... real currentTime/duration/ended/timeupdate events").
 *
 * Mounted ONCE per page that needs it (CampaignContentSetup.jsx and
 * PlaylistSetup.jsx — the two page roots that own the 5 reuse points: the
 * add-content sheet's media/YouTube tabs, and MAIN/vibes/playlist rows +
 * playlist-accordion sub-items). "One track at a time" only needs to hold
 * within a single page's own provider instance — the two pages are never
 * both mounted at once (SPA route switch unmounts the previous page, which
 * runs this provider's own cleanup effect below, stopping any audio/video
 * that was still running — satisfies the task's "on route change" stop
 * condition without a separate router listener).
 */
const MediaPreviewCtx = createContext(null);

const PREVIEWABLE_TYPES = ['IMAGE', 'VIDEO', 'AUDIO', 'GIF', 'CORNER', 'YOUTUBE'];
const VISUAL_TYPES = ['IMAGE', 'VIDEO', 'GIF', 'CORNER'];

export function normType(item) {
  return String(item?.type || '').toUpperCase();
}

export function isPreviewable(item) {
  return PREVIEWABLE_TYPES.includes(normType(item));
}

export function isVisual(item) {
  return VISUAL_TYPES.includes(normType(item));
}

// Same extraction PreviewMediaDialog.jsx's own local helper already uses for
// an existing MAIN-layer YOUTUBE row's `path` (a plain
// "https://youtube.com/watch?v=ID" URL, NOT the add-content sheet's own
// synthetic `{youtubeId}` shape) — reused here so row-thumbnail clicks on an
// already-attached YouTube item open a real embed instead of silently
// falling through (CampaignCard.jsx renders YOUTUBE rows through the same
// <MediaThumb onPreview> wiring as every other media row — confirmed live,
// see MediaController's getCampaignMedia `type`/`path` columns).
export function extractYoutubeId(url = '') {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) return u.pathname.slice(1).split('/')[0];
    if (u.searchParams.get('v')) return u.searchParams.get('v');
    const m = u.pathname.match(/(?:shorts|embed)\/([^/]+)/);
    if (m) return m[1];
  } catch {
    /* not a URL */
  }
  return null;
}

export function MediaPreviewProvider({ children }) {
  const audioElRef = useRef(null);
  if (!audioElRef.current && typeof window !== 'undefined' && typeof Audio !== 'undefined') {
    const el = new Audio();
    el.preload = 'none';
    audioElRef.current = el;
  }

  const [audioItem, setAudioItem] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioPos, setAudioPos] = useState(0);
  const [audioDur, setAudioDur] = useState(0);

  // Real <audio> element event wiring — timeupdate/loadedmetadata/ended/
  // play/pause drive React state, which every consumer (mini-player, tile
  // ring, lightbox audio-card) re-renders from. No fake timer anywhere.
  useEffect(() => {
    const el = audioElRef.current;
    if (!el) return undefined;
    const onTime = () => setAudioPos(el.currentTime || 0);
    const onMeta = () => setAudioDur(el.duration || 0);
    const onEnded = () => { setAudioPlaying(false); setAudioPos(0); };
    const onPlay = () => setAudioPlaying(true);
    const onPause = () => setAudioPlaying(false);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('durationchange', onMeta);
    el.addEventListener('ended', onEnded);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('durationchange', onMeta);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
    };
  }, []);

  const stopAudio = useCallback(() => {
    const el = audioElRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
    setAudioItem(null);
    setAudioPlaying(false);
    setAudioPos(0);
    setAudioDur(0);
  }, []);

  // Route-change / page-unmount stop condition — the provider itself
  // unmounts when the page does (it's mounted inside the page component,
  // not at the app root), so this cleanup is the "stop on route change"
  // behavior the task asks for without inventing a separate router hook.
  useEffect(() => {
    return () => {
      const el = audioElRef.current;
      if (el) {
        el.pause();
        el.removeAttribute('src');
        try { el.load(); } catch { /* jsdom/test env no-op */ }
      }
    };
  }, []);

  const audioItemRef = useRef(audioItem);
  audioItemRef.current = audioItem;

  const playAudio = useCallback((item) => {
    const el = audioElRef.current;
    if (!el || !item?.path) return;
    if (audioItemRef.current !== item) {
      el.src = item.path;
      el.currentTime = 0;
      setAudioItem(item);
      setAudioPos(0);
      setAudioDur(0);
    }
    el.play().catch(() => { /* autoplay-policy rejection — user already clicked, usually fine */ });
  }, []);

  const pauseAudio = useCallback(() => {
    audioElRef.current?.pause();
  }, []);

  const toggleAudio = useCallback((item) => {
    if (audioItemRef.current === item && !audioElRef.current?.paused) pauseAudio();
    else playAudio(item);
  }, [playAudio, pauseAudio]);

  const seekAudio = useCallback((frac) => {
    const el = audioElRef.current;
    const item = audioItemRef.current;
    if (!el || !item) return;
    const d = el.duration || 0;
    if (!d) return;
    el.currentTime = Math.min(1, Math.max(0, frac)) * d;
    setAudioPos(el.currentTime);
    if (el.paused) el.play().catch(() => {});
  }, []);

  const isAudioPlaying = useCallback((item) => audioItem === item && audioPlaying, [audioItem, audioPlaying]);

  // ---------------- lightbox ----------------
  const [lightbox, setLightbox] = useState(null); // { list, index, opts }
  const returnFocusRef = useRef(null);

  const openLightbox = useCallback((list, index, opts = {}) => {
    returnFocusRef.current = typeof document !== 'undefined' ? document.activeElement : null;
    const safeIndex = Math.min(Math.max(index, 0), Math.max(list.length - 1, 0));
    setLightbox({ list, index: safeIndex, opts });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox((lb) => {
      if (lb) {
        const cur = lb.list[lb.index];
        if (cur && normType(cur) === 'AUDIO' && lb.opts?.stopAudioOnClose) {
          stopAudio();
        }
      }
      return null;
    });
    requestAnimationFrame(() => {
      try { returnFocusRef.current?.focus?.(); } catch { /* element may be gone */ }
      returnFocusRef.current = null;
    });
  }, [stopAudio]);

  const goLightbox = useCallback((delta) => {
    setLightbox((lb) => {
      if (!lb) return lb;
      const j = lb.index + delta;
      if (j < 0 || j >= lb.list.length) return lb;
      return { ...lb, index: j };
    });
  }, []);

  const value = useMemo(() => ({
    audio: {
      item: audioItem,
      playing: audioPlaying,
      pos: audioPos,
      dur: audioDur,
      play: playAudio,
      pause: pauseAudio,
      toggle: toggleAudio,
      stop: stopAudio,
      seek: seekAudio,
      isPlaying: isAudioPlaying,
    },
    lightbox,
    openLightbox,
    closeLightbox,
    goLightbox,
  }), [audioItem, audioPlaying, audioPos, audioDur, playAudio, pauseAudio, toggleAudio, stopAudio, seekAudio, isAudioPlaying, lightbox, openLightbox, closeLightbox, goLightbox]);

  return (
    <MediaPreviewCtx.Provider value={value}>
      {children}
      {lightbox && <Lightbox state={lightbox} onClose={closeLightbox} onGo={goLightbox} />}
    </MediaPreviewCtx.Provider>
  );
}

export function useMediaPreview() {
  const ctx = useContext(MediaPreviewCtx);
  if (!ctx) throw new Error('useMediaPreview must be used within a <MediaPreviewProvider>');
  return ctx;
}

/**
 * Non-throwing variant for components shared with call sites that are NOT
 * wrapped in a <MediaPreviewProvider> — e.g. MediaList.jsx/
 * YouTubeSearchList.jsx also render for InteractiveCampaignSetup.jsx and
 * ScreenInfo.jsx (Layouts) via a plain `<AddContentPopup/>` with no
 * `builderRow`-triggering prop, so `isBuilderSheet`/`builderRow` is false
 * there and neither page mounts the provider. Returns null outside a
 * provider; callers must guard on `builderRow` (or the null itself) before
 * using it — this keeps those two unrelated pages' call sites at zero diff
 * instead of forcing them into the provider tree just to avoid a throw.
 */
export function useMediaPreviewOptional() {
  return useContext(MediaPreviewCtx);
}

export function fmtTime(v) {
  v = Math.max(0, Math.round(v || 0));
  const m = Math.floor(v / 60);
  const s = v % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}
